/**
 * A deliberately tiny Markdown reader for the terms document (ALL-5722).
 *
 * It returns plain data, never HTML: the page renders each block as React text
 * nodes, so no markup in the document (a `<script>`, an `<img onerror>`, a raw
 * `<a href="javascript:…">`) can ever reach the DOM as an element. Supported:
 *
 *   - `# ` / `## ` / `### ` lines become headings (levels 1–3),
 *   - consecutive `- ` (or `* `) lines become one list,
 *   - every other run of non-blank lines becomes one paragraph.
 *
 * Inline `[label](url)` links are flattened to `label (url)` plain text, so the
 * reader still sees where a link points but nothing is clickable.
 */

export type TermsBlock =
  | { kind: "heading"; level: 1 | 2 | 3; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; items: string[] };

const LINK = /\[([^\]\n]*)\]\(([^)\s]*)\)/g;

/** Flattens inline links to plain text and trims the line. */
export function inlineText(value: string): string {
  return value.replace(LINK, (_match, label: string, url: string) => {
    const text = label.trim();
    if (!url) return text;
    return text && text !== url ? `${text} (${url})` : url;
  }).trim();
}

export function parseTermsMarkdown(markdown: string): TermsBlock[] {
  const blocks: TermsBlock[] = [];
  let paragraph: string[] = [];
  let list: string[] | null = null;

  const flushParagraph = () => {
    if (paragraph.length) blocks.push({ kind: "paragraph", text: inlineText(paragraph.join(" ")) });
    paragraph = [];
  };
  const flushList = () => {
    if (list && list.length) blocks.push({ kind: "list", items: list });
    list = null;
  };

  for (const raw of String(markdown ?? "").replace(/\r\n?/g, "\n").split("\n")) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flushParagraph();
      flushList();
      continue;
    }
    const heading = /^(#{1,3})\s+(.*)$/.exec(line.trimStart());
    if (heading) {
      flushParagraph();
      flushList();
      blocks.push({ kind: "heading", level: heading[1].length as 1 | 2 | 3, text: inlineText(heading[2]) });
      continue;
    }
    const item = /^[-*]\s+(.*)$/.exec(line.trimStart());
    if (item) {
      flushParagraph();
      if (!list) list = [];
      list.push(inlineText(item[1]));
      continue;
    }
    if (list) {
      // A continuation line of the last list item.
      list[list.length - 1] = `${list[list.length - 1]} ${inlineText(line)}`.trim();
      continue;
    }
    paragraph.push(line.trim());
  }
  flushParagraph();
  flushList();
  return blocks;
}
